import { useCallback, useEffect, useRef, useState } from "react";
// A minimal, valid, silent WAV file, built at runtime rather than hand-encoded as a base64 magic
// string -- see `unlock()`'s own doc comment for why this exists: priming an `<audio>` element with
// no source at all is a no-op on iOS (`.play()` rejects immediately, "no supported source"), so the
// unlock needs something real to actually play.
function createSilentAudioUrl() {
    const sampleRate = 8000;
    const numSamples = 800;
    const buffer = new ArrayBuffer(44 + numSamples);
    const view = new DataView(buffer);
    const writeStr = (offset, str) => {
        for (let i = 0; i < str.length; i++)
            view.setUint8(offset + i, str.charCodeAt(i));
    };
    writeStr(0, "RIFF");
    view.setUint32(4, 36 + numSamples, true);
    writeStr(8, "WAVE");
    writeStr(12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate, true);
    view.setUint16(32, 1, true);
    view.setUint16(34, 8, true);
    writeStr(36, "data");
    view.setUint32(40, numSamples, true);
    for (let i = 0; i < numSamples; i++)
        view.setUint8(44 + i, 128); // silence, 8-bit unsigned PCM midpoint
    return URL.createObjectURL(new Blob([buffer], { type: "audio/wav" }));
}
// Plays real TTS audio (Amazon Polly, via sable-api's `/speak` and `/filler-audio` endpoints --
// proposal sable-interfaces-voice-orb.2026-10-12 §4.3 Phase 3) via a plain `<audio>` element --
// `new Audio()` + `.play()`, no Web Audio API anywhere in this file.
//
// Fourth real architecture for this file, each change driven by a specific confirmed symptom, not a
// guess:
//   1. A plain `<audio>` element, routed through `createMediaElementSource()` into an
//      AnalyserNode/GainNode graph (for the orb's live waveform level) -- popped/clicked on a real
//      phone.
//   2. Decoding to an `AudioBuffer`/`AudioBufferSourceNode` instead, still with the Analyser/Gain
//      graph attached -- STILL popped.
//   3. `AudioBufferSourceNode` with NOTHING else attached (no Analyser/Gain) -- fixed the clicking's
//      worse cousin (continuous "conversation mode" losing the mic after the first exchange, a
//      documented WebKit `HTMLMediaElement` audio-session-lock issue), but kept a small, confirmed
//      "not too bad" click, since starting/stopping a decoded MP3 buffer at a non-zero-crossing
//      sample is itself a waveform discontinuity. A `GainNode` fade-in/out was tried on top of this
//      to remove that click -- confirmed live to make things WORSE (broke playback outright) -- so
//      it was reverted rather than iterated on blind, since this codebase has no way to debug iOS
//      Web Audio behavior other than real device testing.
//   4. (This version.) Confirmed live, directly against `SimpleVoiceTest.tsx`'s debug page (same
//      assistant, side by side): plain `<audio>.play()` with NO Web Audio graph at all sounds
//      noticeably better than `AudioBufferSourceNode` -- no click at all, not just a smaller one.
//      Back to `<audio>` for that reason, which reintroduces the WebKit audio-session-lock risk for
//      continuous mode that #3 was built to avoid -- mitigated (not guaranteed; this needs real
//      device confirmation) by `VoiceOrbExperience.tsx`'s own `speakReply` now waiting a short delay
//      after this hook's `play()` resolves before calling `startListening()` again, giving WebKit a
//      moment to actually release the element's hardware audio-session lock rather than trying to
//      grab the microphone the instant `onended` fires.
export function usePollyAudio() {
    const [speaking, setSpeaking] = useState(false);
    const [error, setError] = useState(null);
    const audioRef = useRef(null);
    const urlRef = useRef(null);
    const finishRef = useRef(null);
    const primedRef = useRef(false);
    const getAudio = useCallback(() => {
        if (!audioRef.current)
            audioRef.current = new Audio();
        return audioRef.current;
    }, []);
    useEffect(() => {
        return () => {
            audioRef.current?.pause();
            if (urlRef.current)
                URL.revokeObjectURL(urlRef.current);
        };
    }, []);
    const stop = useCallback(() => {
        audioRef.current?.pause();
        setSpeaking(false);
        finishRef.current?.();
        finishRef.current = null;
    }, []);
    // Browsers only grant autoplay permission for a brief window tied directly to a real user gesture
    // -- `play()` below is always reached after at least one network round-trip, well outside that
    // window. A real play/pause against a VALID (if silent) source, done synchronously inside the
    // gesture handler, primes the element so a later, gesture-less `.play()` call against a different
    // `src` is allowed -- an element with no `src` at all has nothing to actually play, so `.play()`
    // on it is a no-op/rejects immediately and primes nothing (confirmed live: without a real source
    // here, real audio stopped playing entirely). Only needs to happen once per session.
    const unlock = useCallback(() => {
        if (primedRef.current)
            return;
        primedRef.current = true;
        const audio = getAudio();
        audio.src = createSilentAudioUrl();
        audio
            .play()
            .then(() => audio.pause())
            .catch(() => { });
    }, [getAudio]);
    // Explicitly tears the element's current media resource down (`pause`, drop `src`, `load()`)
    // rather than just letting it sit on whatever it last played -- confirmed live (Mac Safari,
    // reported as "fails to reset after the initial question", same root cause as the earlier iOS
    // bug): WebKit's documented failure to reliably release an `HTMLMediaElement`'s hardware
    // audio-session lock isn't iOS-only, it's the shared WebKit engine, and a passive delay before
    // restarting `SpeechRecognition` is a hope, not a fix. Actively tearing the resource down the
    // instant playback ends is a stronger signal to WebKit that the element is genuinely done with
    // the audio session than leaving it idle with a finished `src` still attached.
    const releaseAudio = (audio) => {
        try {
            audio.pause();
            audio.removeAttribute("src");
            audio.load();
        }
        catch {
            // Nothing to do if even this fails.
        }
    };
    // Resolves once this clip actually finishes (naturally, via error, or via a manual `stop()`) --
    // the filler queue needs to know when it's safe to start the next clip rather than overlapping
    // them, and `speakReply` needs to know when it's safe to restart listening.
    const play = useCallback((blob) => {
        return new Promise((resolve) => {
            unlock();
            setError(null);
            const audio = getAudio();
            if (urlRef.current)
                URL.revokeObjectURL(urlRef.current);
            const url = URL.createObjectURL(blob);
            urlRef.current = url;
            const finish = () => {
                finishRef.current = null;
                resolve();
            };
            finishRef.current = finish;
            audio.onended = () => {
                // Clear both handlers before tearing the resource down -- `releaseAudio`'s own
                // `removeAttribute("src")` + `load()` can itself fire a spurious `error` event in some
                // browsers (no source to load), which would otherwise misreport a real successful
                // playback as a failure right after it finished.
                audio.onended = null;
                audio.onerror = null;
                releaseAudio(audio);
                setSpeaking(false);
                finish();
            };
            audio.onerror = () => {
                audio.onended = null;
                audio.onerror = null;
                setError("Couldn't play that audio.");
                setSpeaking(false);
                finish();
            };
            audio.src = url;
            audio
                .play()
                .then(() => setSpeaking(true))
                .catch(() => {
                setError("Couldn't play audio -- your browser may have blocked autoplay.");
                finish();
            });
        });
    }, [unlock, getAudio]);
    return { speaking, error, play, stop, unlock };
}
